import i18n, { currentLocale } from '@/i18n'

/**
 * 截断 ETH 地址显示: 0x1234...abcd
 */
export function formatAddress(addr: string, front = 6, back = 4): string {
  if (!addr || addr.length < front + back) return addr
  return `${addr.slice(0, front)}...${addr.slice(-back)}`
}

/**
 * 格式化金额：截断（不四舍五入）到指定小数位，带千分位
 */
export function formatAmount(amount: number | string, decimals = 2): string {
  const n = typeof amount === 'string' ? parseFloat(amount) : amount
  if (isNaN(n)) return '0'
  const truncated = Math.trunc(n * Math.pow(10, decimals)) / Math.pow(10, decimals)
  return truncated.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

/**
 * Unix 毫秒时间戳格式化
 */
export function formatTime(timestamp: number): string {
  const d = new Date(timestamp)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/**
 * 倒计时格式化 HH:mm:ss
 */
export function formatCountdown(targetMs: number): string {
  const diff = Math.max(0, targetMs - Date.now())
  const totalSeconds = Math.floor(diff / 1000)
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(h)}:${pad(m)}:${pad(s)}`
}

/**
 * 格式化数字（带千分位）
 */
export function formatNumber(num: number, decimals = 2): string {
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

/**
 * 格式化货币
 */
export function formatCurrency(num: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(num)
}

/**
 * 格式化日期
 */
export function formatDate(date: Date | string | number): string {
  return new Intl.DateTimeFormat(currentLocale().dateLocale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(date))
}

/**
 * 相对时间格式化
 */
export function formatRelativeTime(date: Date | string | number): string {
  const diff = Date.now() - new Date(date).getTime()
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  if (days > 0) return i18n.t('format.daysAgo', { count: days })
  if (hours > 0) return i18n.t('format.hoursAgo', { count: hours })
  if (minutes > 0) return i18n.t('format.minutesAgo', { count: minutes })
  return i18n.t('format.justNow')
}

/**
 * 截断到指定小数位（不四舍五入）
 */
function truncate(n: number, decimals: number): string {
  const factor = Math.pow(10, decimals)
  return (Math.trunc(n * factor) / factor).toFixed(decimals)
}

export interface FormattedWan {
  /** 缩写后的显示值 */
  display: string
  /** 原始精确值（截断4位小数），有缩写时用于 tooltip */
  raw: string
  /** 是否有缩写后缀 */
  abbreviated: boolean
}

/**
 * 格式化大数字缩写，按当前语言习惯，截断不四舍五入，返回 display + raw：
 * - 万制语言（中文「万」）：>=10000 缩写为「万」
 * - 西制语言：>=1e6 缩写 M，>=1e3 缩写 K
 * 缩写习惯由 i18n 的 LOCALES[].numberStyle 决定。
 */
export function formatWan(value: string | number): FormattedWan {
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return { display: '0', raw: '0', abbreviated: false }
  const raw = truncate(num, 4)

  // 去尾零：>=100 取整，否则保留 2 位小数并去掉尾随 0
  const compact = (n: number) =>
    n >= 100 ? truncate(n, 0) : truncate(n, 2).replace(/0+$/, '').replace(/\.$/, '')

  if (currentLocale().numberStyle === 'wan') {
    if (num >= 10000) {
      return { display: compact(num / 10000) + i18n.t('home.wan'), raw, abbreviated: true }
    }
  } else {
    if (num >= 1e6) return { display: compact(num / 1e6) + 'M', raw, abbreviated: true }
    if (num >= 1e3) return { display: compact(num / 1e3) + 'K', raw, abbreviated: true }
  }
  return { display: truncate(num, 4).replace(/\.?0+$/, ''), raw, abbreviated: false }
}
